/**
 * <delivery-estimate> — PDP delivery-date estimator (client-side, in-stock only).
 *
 * Rendered inline in the stock-status line (blocks/product-inventory.liquid). Three layouts:
 * 'promise' (default since 2026-10-24) — ONE line "Get it express by Wed, Oct 28 ⓘ" (express late date;
 * delivery_lead = both (default since 2026-10-25) → "Free by Oct 31 · Overnight Oct 27"; express / free are A/B alternatives); the ⓘ bubble holds "Order by 12 PM to ship today" (shopper's clock) + standard
 * and express dates (bags: no express row); 'both' — "Free shipping as soon as Fri,
 * Jul 10" + "Express — Jul 8–9"; and 'lead' (one line + ⓘ tooltip). Framework-free custom element. On a variant switch Horizon morphs the product
 * section in place (product-inventory.js), which re-hides this element without re-running
 * connectedCallback — so it re-computes on the section's 'variant:update' event (next frame). The
 * server suppresses it for sold-out / pre-order / coming-soon / dropship variants.
 *
 * SLA-safe wording (owner call): the free promise is the EARLIEST arrival with "as soon as" (an honest
 * floor, never a hard date — we don't know the shipping zone); express is a conservative RANGE with a
 * buffer (we don't always dispatch same-day, so a range avoids "not next-day" refund disputes), terse
 * with no "as soon as". Dates are short ("Fri, Jul 10" / "Jul 8–9"). All dates in the warehouse
 * timezone, US-carrier-holiday aware. Packing is Mon–Fri before the 12 PM Eastern cutoff (Sam,
 * 2026-10-24); DELIVERY days are per-tier — standard (UPS/USPS) Mon–Sat, express (FedEx Overnight,
 * "next business day once shipped") Mon–Fri — all editor-tunable. No price / cutoff on the
 * line (revealed at checkout). Free-eligibility (data-free) is bag-aware server-side. The ⓘ is a
 * pure-CSS hover/focus tooltip (no JS positioning). A/B flags: window.PADEL_DELIVERY_VARIANT ('off'
 * hides it) · window.PADEL_DELIVERY_PRESENT ('promise'|'lead'|'both') · window.PADEL_DELIVERY_TRANSIT_MAX (1–90) · window.PADEL_DELIVERY_LEAD
 * ('both'|'free'|'express' — which promise the line makes). Hidden until JS reveals. See features/delivery-date-estimator.md.
 */
(function () {
  if (typeof customElements === 'undefined' || customElements.get('delivery-estimate')) return;

  var WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // ── civil-date helpers (UTC-based so date math is timezone-agnostic once "today" is known) ──
  function civil(y, m, d) { return new Date(Date.UTC(y, m, d)); }
  function addDays(dt, n) { return new Date(dt.getTime() + n * 86400000); }
  function ymd(dt) {
    var m = dt.getUTCMonth() + 1, d = dt.getUTCDate();
    return dt.getUTCFullYear() + '-' + (m < 10 ? '0' + m : m) + '-' + (d < 10 ? '0' + d : d);
  }
  function nthWeekday(year, month, weekday, n) {
    var first = civil(year, month, 1);
    return civil(year, month, 1 + ((7 + weekday - first.getUTCDay()) % 7) + (n - 1) * 7);
  }
  function lastWeekday(year, month, weekday) {
    var last = civil(year, month + 1, 0);
    return civil(year, month, last.getUTCDate() - ((7 + last.getUTCDay() - weekday) % 7));
  }
  function observed(dt) {
    var day = dt.getUTCDay();
    if (day === 6) return addDays(dt, -1);
    if (day === 0) return addDays(dt, 1);
    return dt;
  }
  // Days major US carriers (UPS/FedEx ground) close — skipping them keeps estimates conservative.
  function holidaysForYear(year) {
    return [
      observed(civil(year, 0, 1)), lastWeekday(year, 4, 1), observed(civil(year, 6, 4)),
      nthWeekday(year, 8, 1, 1), nthWeekday(year, 10, 4, 4), observed(civil(year, 11, 25))
    ].map(ymd);
  }
  function holidaySet(years) {
    var set = {};
    for (var i = 0; i < years.length; i++) {
      var list = holidaysForYear(years[i]);
      for (var j = 0; j < list.length; j++) set[list[j]] = true;
    }
    return set;
  }
  function isHolidayOrClosed(dt, holidays, closures) { var k = ymd(dt); return !!holidays[k] || !!closures[k]; }
  // A DELIVERY (transit) day for a given carrier tier. Sat/Sun are opt-in per tier: standard carriers
  // (UPS/USPS) deliver Mon–Sat; express (FedEx 2Day/Overnight) delivers every day incl. Sun. Holidays
  // are always skipped. (Packing/dispatch is a separate Mon–Fri rule — see isDispatchDay.)
  function isTransitDay(dt, holidays, closures, satDelivers, sunDelivers) {
    var wd = dt.getUTCDay();
    if (wd === 0) return !!sunDelivers && !isHolidayOrClosed(dt, holidays, closures);
    if (wd === 6) return !!satDelivers && !isHolidayOrClosed(dt, holidays, closures);
    return !isHolidayOrClosed(dt, holidays, closures);
  }
  function isDispatchDay(dt, holidays, closures, saturday) {
    var wd = dt.getUTCDay();
    if (wd === 0) return false;
    if (wd === 6 && !saturday) return false;
    return !isHolidayOrClosed(dt, holidays, closures);
  }
  function nextDispatchDay(from, holidays, closures, saturday) {
    var dt = addDays(from, 1);
    while (!isDispatchDay(dt, holidays, closures, saturday)) dt = addDays(dt, 1);
    return dt;
  }
  function addTransitDays(from, n, holidays, closures, satDelivers, sunDelivers) {
    var dt = from, count = 0;
    while (count < n) { dt = addDays(dt, 1); if (isTransitDay(dt, holidays, closures, satDelivers, sunDelivers)) count++; }
    return dt;
  }

  function tzParts(date, tz) {
    try {
      var dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
      });
      var o = {};
      dtf.formatToParts(date).forEach(function (p) { o[p.type] = p.value; });
      return { year: +o.year, month: +o.month, day: +o.day, hour: o.hour === '24' ? 0 : +o.hour, minute: +o.minute };
    } catch (e) {
      return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate(), hour: date.getHours(), minute: date.getMinutes() };
    }
  }
  // The UTC instant of a wall-clock time in `tz` (two-pass offset correction, DST-safe).
  function zonedInstant(y, m, d, h, min, tz) {
    var target = Date.UTC(y, m - 1, d, h, min), guess = target;
    for (var i = 0; i < 2; i++) {
      var p = tzParts(new Date(guess), tz);
      guess += target - Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
    }
    return guess;
  }
  // "12 PM" / "9:30 AM" in the SHOPPER's clock — exact in every timezone, and it never names the
  // warehouse or its timezone.
  function fmtLocalTime(ms) {
    try {
      return new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(':00', '');
    } catch (e) { return ''; }
  }
  function setText(el, text) { if (el) el.textContent = text || ''; }
  // Bubble row: label on the left, date on the right. Empty label → empty row (self-hides via :empty).
  function setRow(el, label, value) {
    if (!el) return;
    el.textContent = '';
    if (!label) return;
    var l = document.createElement('span'), v = document.createElement('span');
    l.className = 'delivery-estimate__tip-label'; v.className = 'delivery-estimate__tip-value';
    l.textContent = label; v.textContent = value;
    el.appendChild(l); el.appendChild(v);
  }
  function clampInt(v, def, min, max) {
    var n = parseInt(v, 10);
    if (isNaN(n)) return def;
    return n < min ? min : n > max ? max : n;
  }
  function parseClosures(str) {
    var set = {};
    if (!str) return set;
    str.split(',').forEach(function (s) { s = s.trim(); if (/^\d{4}-\d{2}-\d{2}$/.test(s)) set[s] = true; });
    return set;
  }
  // "Fri, Jul 10" — short weekday + short month + day (the free single-date promise; owner wants dates
  // short to reduce text). No ordinal.
  function fmtMonthDay(dt) { return MONTH_SHORT[dt.getUTCMonth()] + ' ' + dt.getUTCDate(); }
  function fmtShort(dt) { return WEEKDAY_SHORT[dt.getUTCDay()] + ', ' + MONTH_SHORT[dt.getUTCMonth()] + ' ' + dt.getUTCDate(); }
  // Compact express range: "Jul 8–9" (same month), "Jul 30–Aug 1" (cross-month), "Jul 8" (single day).
  function exRangeShort(start, end) {
    var mS = MONTH_SHORT[start.getUTCMonth()], dS = start.getUTCDate();
    if (ymd(start) === ymd(end)) return mS + ' ' + dS;
    var mE = MONTH_SHORT[end.getUTCMonth()], dE = end.getUTCDate();
    return start.getUTCMonth() === end.getUTCMonth()
      ? mS + ' ' + dS + '–' + dE
      : mS + ' ' + dS + '–' + mE + ' ' + dE;
  }

  customElements.define('delivery-estimate', class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      var variant = (window.PADEL_DELIVERY_VARIANT || this.getAttribute('data-default-variant') || 'on')
        .toString().toLowerCase();
      if (variant === 'off' || variant === 'control' || variant === 'false' || variant === '0') {
        this.setAttribute('hidden', '');
        return;
      }

      var leadFlag = (window.PADEL_DELIVERY_LEAD || '').toString().toLowerCase();
      var lead = (leadFlag === 'both' || leadFlag === 'free' || leadFlag === 'express')
        ? leadFlag
        : ((this.getAttribute('data-lead') || 'both').toLowerCase());

      var presentFlag = (window.PADEL_DELIVERY_PRESENT || '').toString().toLowerCase();
      var present = (presentFlag === 'promise' || presentFlag === 'lead' || presentFlag === 'both')
        ? presentFlag
        : ((this.getAttribute('data-present') || 'promise').toLowerCase());

      this.cfg = {
        cutoffHour: clampInt(this.getAttribute('data-cutoff-hour'), 12, 0, 23),
        cutoffMinute: clampInt(this.getAttribute('data-cutoff-minute'), 0, 0, 59),
        showCutoff: this.getAttribute('data-show-cutoff') !== 'false',
        tz: this.getAttribute('data-timezone') || 'America/New_York',
        transitMin: clampInt(this.getAttribute('data-transit-min'), 3, 0, 90),
        transitMax: clampInt(this.getAttribute('data-transit-max'), 5, 0, 90),
        saturday: this.getAttribute('data-saturday') === 'true',
        // Standard carriers (UPS/USPS) deliver Mon–Sat → Saturday counts, Sunday doesn't (defaults).
        transitSat: this.getAttribute('data-transit-saturday') !== 'false',
        transitSun: this.getAttribute('data-transit-sunday') === 'true',
        closures: parseClosures(this.getAttribute('data-closures')),
        express: this.getAttribute('data-express') === 'true',
        expressCutoff: clampInt(this.getAttribute('data-express-cutoff'), 12, 0, 23),
        expressTransit: clampInt(this.getAttribute('data-express-transit'), 1, 0, 60),
        expressBuffer: clampInt(this.getAttribute('data-express-buffer'), 1, 0, 14),
        // Express (FedEx Overnight) delivers on business days — weekends are opt-in (default off).
        expressSat: this.getAttribute('data-express-saturday') === 'true',
        expressSun: this.getAttribute('data-express-sunday') === 'true',
        free: this.getAttribute('data-free') === 'true',
        lead: lead,
        present: present,
        note: (this.getAttribute('data-express-note') || '').trim()
      };
      // A/B (snippet's "Delivery date test"): the "by" date's transit days for this visitor's arm.
      var maxFlag = parseInt(window.PADEL_DELIVERY_TRANSIT_MAX, 10);
      if (maxFlag >= 1 && maxFlag <= 90) this.cfg.transitMax = Math.max(maxFlag, this.cfg.transitMin);
      if (this.cfg.transitMax < this.cfg.transitMin) {
        var t = this.cfg.transitMin; this.cfg.transitMin = this.cfg.transitMax; this.cfg.transitMax = t;
      }
      this.compute();
      this.reportTest();

      // Horizon re-renders the product section on variant change: product-inventory.js runs
      // morph(this, newInventory, {childrenOnly:true}), which re-applies the server `hidden` + blanks
      // this element's spans/class IN PLACE — connectedCallback does NOT re-run (morph preserves the
      // node). Without this, the estimate vanishes on a variant switch. Mirror product-inventory.js:
      // listen for 'variant:update' on the section and re-compute on the next frame (after the morph
      // settles, so our fill/reveal isn't overwritten). The element isn't disconnected across morphs,
      // so the listener persists; guard so it's bound once.
      if (!this._deOnVariant && typeof this.closest === 'function') {
        var self = this;
        this._deOnVariant = function () {
          requestAnimationFrame(function () { if (self.isConnected && self.cfg) self.compute(); });
        };
        this._deSection = this.closest('.shopify-section, dialog');
        if (this._deSection) this._deSection.addEventListener('variant:update', this._deOnVariant);
      }
    }

    // Delivery date A/B test (window.__dlTest from the snippet; contract: padelusa-os
    // tools/storefront-analytics/docs/experiments.md). Once per page: pusa_delivery_assigned (fresh
    // assignment) and pusa_delivery_shown, the cart attribute _delivery_test = <test>:<arm> (qa for
    // previews; also merged into the shared `_ab` by window.PusaAB, snippets/pusa-ab.liquid) so the order
    // carries the arm, and pusa_delivery_atc on the first PDP add-to-cart.
    // Never throws into the widget.
    reportTest() {
      var t = window.__dlTest;
      var kit = window.PusaAB;
      if (!t || !t.arm || !kit || window.__dlReported || this.hasAttribute('hidden')) return;
      if (!t.qa && !t.id) return;
      window.__dlReported = true;
      if (t.fresh) kit.exposure(t, 'assigned');
      kit.exposure(t, 'shown');
      var want = kit.pair(t);
      var stamped = '';
      try { stamped = sessionStorage.getItem('dl_stamped') || ''; } catch (error) {}
      var stamp = function () {
        kit.stamp(t, { _delivery_test: want }).then(function (ok) {
          if (ok) { try { sessionStorage.setItem('dl_stamped', want); } catch (error) {} }
        });
      };
      if ((this.getAttribute('data-attr-test') !== want && stamped !== want) || !kit.hasPair(t)) stamp();
      var atcSent = false;
      document.addEventListener('cart:update', function (event) {
        var d = event && event.detail && event.detail.data;
        if (atcSent || !d || d.didError || d.source !== 'product-form-component') return;
        atcSent = true;
        kit.exposure(t, 'atc');
        // A fresh cart (first add of the visit) may not carry the stamp yet.
        var attrs = event.detail.resource && event.detail.resource.attributes;
        if (!attrs || attrs._delivery_test !== want || String(attrs._ab || '').split(',').indexOf(want) === -1) stamp();
      });
    }

    // One timer to the cutoff instant, so "ships today" never outlives the cutoff on an open tab. The
    // line changes once; nothing ticks (DESIGN.md rule 7).
    scheduleCutoff(ms) {
      if (this._deTimer) { clearTimeout(this._deTimer); this._deTimer = null; }
      var wait = ms - Date.now();
      if (!(wait > 0) || wait > 86400000) return;
      var self = this;
      this._deTimer = setTimeout(function () { self._deTimer = null; if (self.isConnected && self.cfg) self.compute(); }, wait + 1000);
    }

    disconnectedCallback() {
      if (this._deTimer) { clearTimeout(this._deTimer); this._deTimer = null; }
      if (this._deSection && this._deOnVariant) {
        this._deSection.removeEventListener('variant:update', this._deOnVariant);
      }
      // The guided purchase moves the existing stock row into the form. Rebind on reconnect.
      this._deSection = null;
      this._deOnVariant = null;
    }

    compute() {
      // Re-query hooks + refresh the per-variant flag every run: the section morph can replace these
      // child nodes and updates data-free for the newly selected variant.
      this.dateEl = this.querySelector('[data-de="date"]');
      this.secondEl = this.querySelector('[data-de="second"]');
      this.tipShip = this.querySelector('[data-de="tip-ship"]');
      this.tipFree = this.querySelector('[data-de="tip-free"]');
      this.tipExpress = this.querySelector('[data-de="tip-express"]');
      this.cfg.free = this.getAttribute('data-free') === 'true';
      var cfg = this.cfg;
      var p = tzParts(new Date(), cfg.tz);
      var holidays = holidaySet([p.year, p.year + 1]);
      var today = civil(p.year, p.month - 1, p.day);
      var nowMinutes = p.hour * 60 + p.minute;
      var isBiz = isDispatchDay(today, holidays, cfg.closures, cfg.saturday);
      var shipsToday = isBiz && nowMinutes < cfg.cutoffHour * 60 + cfg.cutoffMinute;

      // Standard / free window. 'promise' quotes the transit min–max RANGE (the checkout's own window);
      // 'both'/'lead' quote the EARLIEST arrival with "as soon as" — an honest floor.
      var dispatch = shipsToday ? today : nextDispatchDay(today, holidays, cfg.closures, cfg.saturday);
      var fStart = addTransitDays(dispatch, cfg.transitMin, holidays, cfg.closures, cfg.transitSat, cfg.transitSun);
      var fEnd = addTransitDays(dispatch, cfg.transitMax, holidays, cfg.closures, cfg.transitSat, cfg.transitSun);
      var freeShort = fmtShort(fStart); // "Fri, Jul 10"

      // Express window — earlier cutoff, faster transit, PLUS a buffer so we quote a conservative RANGE.
      // We don't always dispatch same-day; a range ("Jul 8–9") protects the SLA and avoids
      // "it didn't arrive next-day" refund disputes. No "tomorrow" wording for the same reason.
      var exRange = null, exStart = null, exEnd = null;
      if (cfg.express) {
        var exDispatch = (isBiz && nowMinutes < cfg.expressCutoff * 60)
          ? today : nextDispatchDay(today, holidays, cfg.closures, cfg.saturday);
        exStart = addTransitDays(exDispatch, cfg.expressTransit, holidays, cfg.closures, cfg.expressSat, cfg.expressSun);
        exEnd = addTransitDays(exDispatch, cfg.expressTransit + cfg.expressBuffer, holidays, cfg.closures, cfg.expressSat, cfg.expressSun);
        exRange = exRangeShort(exStart, exEnd);
      }

      // Layout branch. Two-line ("both", Amazon-style): the free/timing promise on the stock-status row,
      // plus an express row beneath WHEN express applies (bags have no express → line 1 only, no row 2).
      var exLabel = cfg.note ? (cfg.note.charAt(0).toUpperCase() + cfg.note.slice(1)) : 'Express';

      this.classList.toggle('delivery-estimate--promise', cfg.present === 'promise');
      this.classList.toggle('delivery-estimate--both', cfg.present === 'both');
      // The bubble exists in 'promise' always and in 'lead' only when express is on.
      this.classList.toggle('delivery-estimate--noinfo', cfg.present === 'lead' && !cfg.express);
      setText(this.tipShip, '');
      if (this._deTimer) { clearTimeout(this._deTimer); this._deTimer = null; }

      if (cfg.present === 'promise') {
        // One assertive line, Amazon-style (owner, 2026-10-24): "Order now, arrives free Sat, Oct 31".
        // The single date is the LATEST standard arrival (transit max) — underpromise, overdeliver; the
        // bubble carries the cutoff and the full ranges. "free" only when the variant ships free.
        var shipDay = '', cutoffLocal = '';
        if (shipsToday) {
          var cutoffMs = zonedInstant(p.year, p.month, p.day, cfg.cutoffHour, cfg.cutoffMinute, cfg.tz);
          cutoffLocal = fmtLocalTime(cutoffMs);
          this.scheduleCutoff(cutoffMs);
        } else {
          var gap = Math.round((dispatch.getTime() - today.getTime()) / 86400000);
          shipDay = gap <= 6 ? WEEKDAY_LONG[dispatch.getUTCDay()] : fmtShort(dispatch);
        }
        var stdRange = exRangeShort(fStart, fEnd);
        // Which promise the line makes (delivery_lead; window.PADEL_DELIVERY_LEAD overrides for tests).
        // Every date quoted is the LATE end of its range ("by"), so it is a promise we keep: backtest of
        // 94 orders (Oct 8–16, 2026) — free late date kept 96%, express late date 7 of 8 (the miss was a
        // warehouse dispatch slip). See docs/delivery-promise-directions-2026-10-24.md, "Final pick".
        //   'both' (default, Sam 2026-10-25): "Free by Oct 31 · Overnight Oct 27" — value AND the
        //          fastest option in one line. The overnight date is the express range START (next
        //          business day after dispatch; Sam: show the fastest possible). Backtest: overnight
        //          orders landed that day 5 of 7; both misses were warehouse dispatch slips. No "by"
        //          and no weekdays so it fits one 375px row; each half nowraps, so a narrower phone
        //          drops the overnight half to a clean second row.
        //   'express': "Get it express by Wed, Oct 28"   'free': "Order now, arrives free Sat, Oct 31"
        // Bags (no express) in every mode: "Free by Sat, Oct 31" / "Delivery by Sat, Oct 31".
        var hasEx = cfg.express && exEnd;
        var mode = hasEx ? cfg.lead : 'single';
        var line = this.dateEl;
        var part = function (pre, date) {
          var wrap = document.createElement('span');
          wrap.className = 'delivery-estimate__part';
          wrap.textContent = pre;
          var when = document.createElement('span');
          when.className = 'delivery-estimate__when';
          when.textContent = date;
          wrap.appendChild(when);
          line.appendChild(wrap);
        };
        var freeWord = cfg.free ? 'Free by ' : 'Delivery by ';
        if (line) {
          line.textContent = '';
          if (mode === 'express') {
            part('Get it express by ', fmtShort(exEnd));
          } else if (mode === 'free') {
            part('Order now, arrives ' + (cfg.free ? 'free ' : ''), fmtShort(fEnd));
          } else if (mode === 'single') {
            part(freeWord, fmtShort(fEnd));
          } else {
            part(freeWord, fmtMonthDay(fEnd));
            var sep = document.createElement('span');
            sep.className = 'delivery-estimate__mid';
            sep.setAttribute('aria-hidden', 'true');
            sep.textContent = ' · ';
            line.appendChild(sep);
            part('Overnight ', fmtMonthDay(exStart));
          }
        }
        var fastest = mode === 'express';
        if (this.secondEl) this.secondEl.textContent = '';
        // ⓘ bubble — the detail: the cutoff in the shopper's clock, then standard and express dates.
        setText(this.tipShip, shipsToday
          ? ((cfg.showCutoff && cutoffLocal) ? 'Order by ' + cutoffLocal + ' to ship today' : 'Order today to ship today')
          : 'Ships ' + shipDay);
        // Bubble rows follow the line: express first only when the line leads with express.
        var stdLabel = cfg.free ? 'Free shipping' : 'Standard shipping';
        var exRowLabel = cfg.express && exRange ? (mode === 'both' ? 'Overnight' : exLabel) : '';
        if (mode === 'both' && exStart) exRange = fmtMonthDay(exStart);
        if (fastest) {
          setRow(this.tipFree, exRowLabel, exRange || '');
          setRow(this.tipExpress, stdLabel, stdRange);
        } else {
          setRow(this.tipFree, stdLabel, stdRange);
          setRow(this.tipExpress, exRowLabel, exRange || '');
        }
      } else if (cfg.present === 'both') {

        // Line 1 — conservative promise. Only say "Free delivery" when actually free; otherwise show
        // TIMING only ("Get it as soon as …") — no price, no shipping-cost language (owner: bags/paid
        // shipping is never surfaced on the PDP; free depends on cart value, which we can't see here).
        if (this.dateEl) {
          this.dateEl.textContent = (cfg.free ? 'Free shipping as soon as ' : 'Get it as soon as ') + freeShort;
        }
        // Line 2 — express, terse (no "get it as soon as" — owner: too much text). Only when express
        // applies; the row/tooltip aren't even rendered for bags (data-express=false), so secondEl is null.
        if (this.secondEl) {
          if (cfg.express && exRange) {
            this.secondEl.textContent = exLabel + ' — ' + exRange;
          } else {
            this.secondEl.textContent = '';
          }
        }
      } else {
        if (this.secondEl) this.secondEl.textContent = '';

        // Main line — assertive "Order today" CTA. Express-led → the express range (terse, no
        // "as soon as"); free-led → the free full date + "with free delivery" (the owner's urgency variant).
        var mainText;
        if (cfg.express && cfg.lead === 'express') {
          mainText = 'Order today and get it ' + exRange;
        } else {
          mainText = 'Order today and get it as soon as ' + freeShort;
          if (cfg.free) mainText += ' with free shipping';
        }
        if (this.dateEl) this.dateEl.textContent = mainText;

        // ⓘ tooltip — the fuller picture (only rendered when express is on). "as soon as" on free only.
        setText(this.tipFree, 'Free shipping — as soon as ' + freeShort);
        setText(this.tipExpress, exRange ? 'Express — ' + exRange : '');
      }

      this.removeAttribute('hidden');
    }
  });
})();
